import React, { useCallback, useEffect, useRef, useState } from "react"

// Rail de grandes cartes verticales façon Apple (« Get to know Mac ») :
// photo plein cadre, accroche + titre posés en haut, la carte suivante dépasse
// à droite, bouton « + » qui ouvre le détail, flèches rondes sous le rail.
// Variante validée par Pierre-Axel le 20/10/2026 (section spa Maison Bauchart).
// Tout le texte est rendu dans le HTML (SEO). Défilement natif aimanté
// (trackpad, molette horizontale, doigt), flèches = défilement d'une carte.
//
// items: [{ img, alt, eyebrow, title, detail? }]
// labels: { prev, next, open, close }  — textes d'accessibilité
// inset : marge gauche du rail, alignée sur le conteneur de la page

const STYLE = `
  .cr-wrap { position:relative; }
  .cr-rail { display:flex; gap:20px; overflow-x:auto; scroll-snap-type:x mandatory; scroll-behavior:smooth; overscroll-behavior-x:contain; padding:8px var(--cr-inset) 8px var(--cr-inset); scroll-padding-left:var(--cr-inset); scrollbar-width:none; -webkit-overflow-scrolling:touch; }
  .cr-rail::-webkit-scrollbar { display:none; }
  .cr-card { position:relative; flex:0 0 auto; width:clamp(272px,31vw,420px); aspect-ratio:9/16.2; max-height:760px; border-radius:28px; overflow:hidden; background:#1d1d1f; scroll-snap-align:start; box-shadow:0 1px 2px rgba(0,0,0,0.04); }
  .cr-card img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; transform:scale(1.001); transition:transform 900ms cubic-bezier(0.22,1,0.36,1); }
  .cr-card:hover img { transform:scale(1.04); }
  .cr-scrim { position:absolute; inset:0; background:linear-gradient(180deg,rgba(0,0,0,0.62) 0%,rgba(0,0,0,0.22) 30%,rgba(0,0,0,0) 52%,rgba(0,0,0,0) 78%,rgba(0,0,0,0.28) 100%); pointer-events:none; }
  .cr-text { position:absolute; left:0; right:0; top:0; padding:30px 28px; color:#fff; }
  .cr-eyebrow { display:block; font-size:clamp(15px,1.2vw,17px); font-weight:600; letter-spacing:-0.01em; margin-bottom:8px; text-shadow:0 1px 12px rgba(0,0,0,0.25); }
  .cr-title { font-size:clamp(24px,2.3vw,32px); font-weight:600; letter-spacing:-0.035em; line-height:1.12; margin:0; text-shadow:0 1px 16px rgba(0,0,0,0.3); }
  .cr-plus { position:absolute; right:18px; bottom:18px; width:40px; height:40px; border-radius:50%; border:0; background:rgba(51,51,54,0.72); backdrop-filter:blur(12px); -webkit-backdrop-filter:blur(12px); color:#fff; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:transform 220ms cubic-bezier(0.22,1,0.36,1), background 220ms; }
  .cr-plus:hover { transform:scale(1.08); background:rgba(29,29,31,0.9); }
  .cr-nav { display:flex; justify-content:flex-end; gap:14px; padding:22px var(--cr-inset) 0; }
  .cr-btn { width:44px; height:44px; border-radius:50%; border:0; background:#e8e8ed; color:#1d1d1f; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:background 220ms, opacity 220ms; }
  .cr-btn:hover:not(:disabled) { background:#dcdce1; }
  .cr-btn:disabled { opacity:0.42; cursor:default; }
  .cr-modal { position:fixed; inset:0; z-index:10050; background:rgba(0,0,0,0.55); backdrop-filter:blur(18px); -webkit-backdrop-filter:blur(18px); display:flex; align-items:center; justify-content:center; padding:24px; animation:crFade 260ms ease; }
  .cr-sheet { position:relative; width:min(980px,100%); max-height:calc(100vh - 48px); overflow:auto; border-radius:28px; background:#fff; color:#1d1d1f; animation:crRise 420ms cubic-bezier(0.22,1,0.36,1); }
  .cr-sheet img { display:block; width:100%; aspect-ratio:16/9; object-fit:cover; }
  .cr-sheet-body { padding:32px 40px 40px; }
  .cr-sheet-eyebrow { font-size:17px; font-weight:600; color:#6e6e73; margin-bottom:8px; display:block; }
  .cr-sheet-title { font-size:clamp(26px,3vw,40px); font-weight:600; letter-spacing:-0.04em; line-height:1.1; margin:0 0 16px; }
  .cr-sheet-detail { font-size:18px; line-height:1.6; color:#3d3d3f; margin:0; }
  .cr-close { position:absolute; top:16px; right:16px; width:40px; height:40px; border-radius:50%; border:0; background:rgba(29,29,31,0.72); color:#fff; display:flex; align-items:center; justify-content:center; cursor:pointer; z-index:2; }
  .cr-sheet-nav { display:flex; gap:12px; margin-top:28px; }
  @keyframes crFade { from { opacity:0 } to { opacity:1 } }
  @keyframes crRise { from { opacity:0; transform:translateY(24px) scale(0.98) } to { opacity:1; transform:none } }
  @media (max-width: 734px) {
    .cr-rail { gap:14px; }
    .cr-card { width:78vw; border-radius:24px; }
    .cr-text { padding:24px 22px; }
    .cr-sheet-body { padding:24px 22px 28px; }
    .cr-sheet-detail { font-size:17px; }
  }
  @media (prefers-reduced-motion: reduce) {
    .cr-rail { scroll-behavior:auto; }
    .cr-card img, .cr-modal, .cr-sheet { transition:none !important; animation:none !important; }
  }
`

const Chevron = ({ dir }) => (
  <svg width="10" height="18" viewBox="0 0 10 18" fill="none" aria-hidden="true">
    <path d={dir === "left" ? "M8.5 1.5 1.5 9l7 7.5" : "M1.5 1.5 8.5 9l-7 7.5"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)
const Plus = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
)
const Cross = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M2 2l10 10M12 2 2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
)

const CardRail = ({ items, labels = {}, inset = "max(24px, calc((100vw - 1100px) / 2 + 24px))" }) => {
  const railRef = useRef(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)
  const [open, setOpen] = useState(null)
  const L = { prev: "Précédent", next: "Suivant", open: "En savoir plus", close: "Fermer", ...labels }

  const update = useCallback(() => {
    const el = railRef.current
    if (!el) return
    setAtStart(el.scrollLeft < 8)
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 8)
  }, [])

  useEffect(() => {
    const el = railRef.current
    if (!el) return
    update()
    el.addEventListener("scroll", update, { passive: true })
    window.addEventListener("resize", update)
    return () => { el.removeEventListener("scroll", update); window.removeEventListener("resize", update) }
  }, [update])

  const step = dir => {
    const el = railRef.current
    if (!el) return
    const card = el.querySelector(".cr-card")
    const w = card ? card.getBoundingClientRect().width + 20 : el.clientWidth * 0.8
    el.scrollBy({ left: dir * w, behavior: "smooth" })
  }

  useEffect(() => {
    if (open === null) return
    const onKey = e => {
      if (e.key === "Escape") setOpen(null)
      if (e.key === "ArrowRight") setOpen(i => (i + 1) % items.length)
      if (e.key === "ArrowLeft") setOpen(i => (i - 1 + items.length) % items.length)
    }
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    window.addEventListener("keydown", onKey)
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prevOverflow }
  }, [open, items.length])

  const cur = open !== null ? items[open] : null

  return (
    <div className="cr-wrap" style={{ "--cr-inset": inset }}>
      <style>{STYLE}</style>
      <div className="cr-rail" ref={railRef} role="list">
        {items.map((it, i) => (
          <article key={it.img + i} className="cr-card" role="listitem">
            <img src={it.img} alt={it.alt || it.title} loading="lazy" decoding="async" />
            <div className="cr-scrim" />
            <div className="cr-text">
              {it.eyebrow ? <span className="cr-eyebrow">{it.eyebrow}</span> : null}
              <h3 className="cr-title" dangerouslySetInnerHTML={{ __html: it.title }} />
            </div>
            {it.detail ? (
              <button type="button" className="cr-plus" aria-label={`${L.open} — ${it.eyebrow || ""}`} onClick={() => setOpen(i)}><Plus /></button>
            ) : null}
            {it.detail ? <p style={{ position:"absolute", width:1, height:1, overflow:"hidden", clip:"rect(0 0 0 0)", margin:0 }}>{it.detail}</p> : null}
          </article>
        ))}
      </div>
      <div className="cr-nav">
        <button type="button" className="cr-btn" aria-label={L.prev} disabled={atStart} onClick={() => step(-1)}><Chevron dir="left" /></button>
        <button type="button" className="cr-btn" aria-label={L.next} disabled={atEnd} onClick={() => step(1)}><Chevron dir="right" /></button>
      </div>

      {cur ? (
        <div className="cr-modal" role="dialog" aria-modal="true" aria-label={cur.eyebrow} onClick={() => setOpen(null)}>
          <div className="cr-sheet" onClick={e => e.stopPropagation()}>
            <button type="button" className="cr-close" aria-label={L.close} onClick={() => setOpen(null)}><Cross /></button>
            <img src={cur.img} alt={cur.alt || cur.title} />
            <div className="cr-sheet-body">
              {cur.eyebrow ? <span className="cr-sheet-eyebrow">{cur.eyebrow}</span> : null}
              <h3 className="cr-sheet-title" dangerouslySetInnerHTML={{ __html: cur.title }} />
              <p className="cr-sheet-detail">{cur.detail}</p>
              <div className="cr-sheet-nav">
                <button type="button" className="cr-btn" aria-label={L.prev} onClick={() => setOpen((open - 1 + items.length) % items.length)}><Chevron dir="left" /></button>
                <button type="button" className="cr-btn" aria-label={L.next} onClick={() => setOpen((open + 1) % items.length)}><Chevron dir="right" /></button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default CardRail
